<script lang="ts">
    import AlternativeDashboardPage from '@Alternatives/Alternatives/Pages/AlternativeDashboardPage'
    export default AlternativeDashboardPage
</script>

<template>
    <div>
        <AlternativeEditorDialog :viewModel="alternativeEditorDialog" />

        <EmailSender :viewModel="emailSender" 
                     @form-submit="handleEmailSenderFormSubmit" />

        <NoteLogger :viewModel="noteLogger" 
                    @create-note="handleCreateNoteCommand" />

        <LogsBrowser :viewModel="logsBrowser" />

        <AlternativeRelationshipsDialog v-if="alternativeRelationshipsDialog"
                                        :viewModel="alternativeRelationshipsDialog"
                                        @load-alternatives="loadAlternativesForDependencyDialog"
                                        @unlink-dependent="handleUnlinkDependentAlternativeCommand" 
                                        @unlink-foundation="handleUnlinkFoundationAlternativeCommand" 
                                        @link-foundation="handleLinkFoundationAlternativeCommand" 
                                        @link-dependent="handleLinkDependentAlternativeCommand" />

        <MonthPickerDialog :viewModel="startDateMonthPicker" 
                           @update-date="handleUpdateAlternativeStartDateCommand" />

        <MonthPickerDialog :viewModel="commitmentDateMonthPicker" 
                           @update-date="handleUpdateAlternativeCommitmentDateCommand" />

        <PortfolioOrDecisionNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="projectId" />
        <Navigation v-else-if="isProjectOwner" />

        <div class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">{{alternativeName}}</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">

                    <a class="btn btn-success mr-1" style="padding: 0.7em 2em;" v-sanitizedHref="`/#/q/project/summary/${workspaceId}/${projectId}/${alternativeId}`">Business case</a>

                    <div class="btn-group mr-1" v-if="isWorkspaceMember">
                        <button type="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="showAlternativeEditor(null)" :disabled="rm == null">Edit</button>
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split" data-toggle="dropdown" aria-expanded="false">
                            <span class="sr-only">Toggle Dropdown</span>
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <h6 class="dropdown-header">Custom forms</h6>
                            <a class="dropdown-item" v-for="f in forms" :key="f.Id" @click="showAlternativeEditor(f.Id)">{{f.Name}}</a>
                        </div>
                    </div>

                    <div class="btn-group mr-1" v-else>
                        <button type="button"
                                class="btn btn-outline-secondary dropdown-toggle"
                                style="padding: 0.7em 2em;"
                                data-toggle="dropdown"
                                aria-expanded="false">
                            Edit
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <h6 class="dropdown-header">Custom forms</h6>
                            <template v-if="forms.length">
                                <a class="dropdown-item"
                                   v-for="f in forms"
                                   :key="f.Id"
                                   @click="showAlternativeEditor(f.Id)">
                                    {{ f.Name }}
                                </a>
                            </template>
                            <span class="dropdown-item text-muted" v-else>No custom forms available</span>
                        </div>
                    </div>

                    <a v-if="isWorkspaceMember" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" :href="`/#/sharing/resource/AlternativesDataShareResourceProvider/${alternativeId}`">
                        Share <span v-if="resourceActiveShares.length">({{resourceActiveShares.length}})</span>
                    </a>
                </div>
            </div>
            <hr />

            <div v-if="rm" class="row" style="height: 100vh;">
                <div class="col-xl-9 col-lg-8 col-md-7 col-sm-6">
                    <div class="input-group">
                        <input type="search" v-model="searchPhrase" placeholder="Search..." class="form-control">
                        <span class="input-group-append"><span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span></span>
                    </div>
                    <br />

                    <div class="dashboard-box">
                        <div>
                            <strong>Status</strong>
                            <div>{{rm.StatusName}}</div>
                        </div>

                        <div class="mt-3">
                            <strong>Created</strong><br />
                            <div>{{rm.Created}}</div>
                        </div>

                        <div class="mt-3">
                            <strong>Start date</strong><br />
                            <div>
                                <a class="text-primary" @click="showStartDateMonthPicker">
                                    <span v-if="startDate">{{ startDateLabel }}</span>
                                    <span v-else class="font-italic">Not defined</span>
                                </a>
                            </div>
                            <!--<div v-else :title="'Only workspace members can edit'">
                                <span v-if="startDate">{{ startDateLabel }}</span>
                                <span v-else class="font-italic">Not defined</span>
                            </div>-->
                        </div>

                        <div class="mt-3">
                            <strong>Commitment date</strong><br />
                            <div>
                                <a class="text-primary" @click="showCommitmentDateMonthPicker">
                                    <span v-if="commitmentDate">{{ commitmentDateLabel }}</span>
                                    <span v-else class="font-italic">Not defined</span>
                                </a>
                            </div>
                            <!--<div v-else :title="'Only workspace members can edit'">
                                <span v-if="commitmentDate">{{ commitmentDateLabel }}</span>
                                <span v-else class="font-italic">Not defined</span>
                            </div>-->
                        </div>


                        <div v-for="attr in attributesBySection['']" :key="attr.AttributeId" class="mt-3">
                            <AttributeDataView :readModel="attr" />
                        </div>
                    </div>

                    <div v-for="s in rm.Sections" :key="s.Title">
                        <div class="dashboard-box" v-if="s.Title">
                            <h4>{{s.Title}}</h4>
                            <hr />
                            <div v-for="attr in attributesBySection[s.Title]" :key="attr.AttributeId" class="mt-3">
                                <AttributeDataView :readModel="attr" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-xl-3 col-lg-4 col-md-5 col-sm-6">
                    <div class="dashboard-box">
                        <h4>Description</h4>
                        <hr />
                        <div>
                            <span v-if="rm.Description" v-html="marked(rm.Description)"></span>
                            <span v-else class="text-muted font-italic">No description provided</span>
                        </div>
                    </div>

                    <div class="dashboard-box">
                        <h4>Contacts</h4>
                        <hr />
                        <div>
                            <div v-for="c in emailContacts">
                                <div class="d-flex align-items-center mb-2">
                                    <button @click="showEmailSender(c)" class="btn btn-outline-secondary btn-sm">
                                        <span class="fa fa-envelope"></span>
                                    </button>

                                    <span>
                                        <strong>{{c.Name}}</strong><br />
                                        <small class="text-muted">{{c.Email}}</small>
                                    </span>
                                </div>
                            </div>
                            <p class="text-center mt-4">
                                <button class="btn btn-outline-secondary" @click="showEmailSender(null)">Send email</button>
                            </p>
                        </div>
                    </div>
                    <div class="dashboard-box">
                        <h4>Last activity</h4>
                        <hr />
                        <div v-if="!lastLogRm"><i class="fa fa-spinner fa-spin"></i> Loading...</div>
                        <div v-else>
                            <p>
                                <strong>{{lastLogRm.Title}}</strong><br />
                                <small class="text-muted">
                                    <span v-html="getHumanizedTimeSince(lastLogRm.Created)"></span> ago
                                    by {{lastLogRm.Person}}
                                </small>
                            </p>

                            <p class="text-center mt-4">
                                <button class="btn btn-outline-secondary mr-1" @click="showLogsBrowser">View logs</button>
                                <button class="btn btn-outline-secondary" @click="handleShowNoteLogger">Log a note</button>
                            </p>
                        </div>
                    </div>
                    <div class="dashboard-box">
                        <h3>Files</h3>
                        <hr />
                        <div v-if="attachmentsRm">
                            <div v-for="at in attachmentsView" v-if="!file">
                                <div style="margin-bottom: 10px;" class="clearfix">
                                    <a v-sanitizedHref="at.Url" target="_blank">{{at.Title}}</a>

                                    <div class="dropdown pull-right">
                                        <a data-toggle="dropdown" class="pointer">
                                            <span class="fa fa-bars"></span>
                                        </a>
                                        <div class="dropdown-menu dropdown-menu-right">
                                            <a class="dropdown-item" @click="handleDeleteAttachedDocumentCommand(at)">Delete</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <p class="text-center mt-4">
                                <FileUploader @selected="file=$event" @unselected="file=null" @uploaded="handleAddAttachedDocumentCommand" />
                            </p>
                        </div>
                        <div v-else>
                            <i class="fa fa-spinner fa-spin"></i> Loading...
                        </div>
                    </div>
                    <div class="dashboard-box">
                        <h3>Dependencies</h3>
                        <hr />
                        <div>
                            <p>
                                This {{rm.AlternativeLabel.toLowerCase()}} is a foundation for {{rm.Dependents.length}} {{rm.AlternativesLabel.toLowerCase()}} and is
                                dependent on {{rm.Foundations.length}} {{rm.AlternativesLabel.toLowerCase()}}.
                            </p>
                            <p class="text-center mt-4">
                                <button class="btn btn-outline-secondary" @click="showDependenciesManager">Manage</button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .dashboard-box {
        background-color: white;
        padding: 10px 20px;
        border-radius: 2px;
        box-shadow: 0 1px 5px 0 rgba(45,62,80,0.12);
        margin-bottom: 2vh;
    }
</style>


